import { API_BASE, request } from '@/src/infrastructure/lib/api';

export type CanonicalAspectRatio = 'square' | 'portrait' | 'landscape' | 'story';
export const ASPECT_RATIOS: CanonicalAspectRatio[] = ['square', 'portrait', 'landscape', 'story'];

export interface AiArtifactStyle {
  id: string;
  occasion: string;
  name: string;
  promptTemplate: string;
  thumbnailUrl?: string | null;
  defaultTextRenderMode: 'overlay' | 'baked' | string;
  imageProvider: 'pulid-flux' | 'gemini' | 'openai' | string;
  maxFaces: number;
  active: boolean;
  allowedAspectRatios: CanonicalAspectRatio[];
  defaultAspectRatio: CanonicalAspectRatio;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAiStylePayload {
  id: string;
  occasion: string;
  name: string;
  promptTemplate: string;
  thumbnailUrl?: string;
  defaultTextRenderMode?: string;
  imageProvider?: string;
  maxFaces?: number;
  active?: boolean;
  allowedAspectRatios?: CanonicalAspectRatio[];
  defaultAspectRatio?: CanonicalAspectRatio;
}

export interface UpdateAiStylePayload {
  occasion?: string;
  name?: string;
  promptTemplate?: string;
  thumbnailUrl?: string;
  defaultTextRenderMode?: string;
  imageProvider?: string;
  maxFaces?: number;
  active?: boolean;
  allowedAspectRatios?: CanonicalAspectRatio[];
  defaultAspectRatio?: CanonicalAspectRatio;
}


export interface AiGenerationStats {
  totalGenerations: number;
  totalCostUsd: number;
  readyCount: number;
  failedCount: number;
  successRate: number;
  avgLatencyMs: number;
  providerStats: Record<string, { count: number; costUsd: number; readyCount: number }>;
  occasionStats: Record<string, { count: number; costUsd: number }>;
}

export interface AiGenerationLog {
  id: string;
  userId: string;
  occasion: string;
  styleId: string;
  aspectRatio: string;
  status: string;
  provider?: string | null;
  providerCost?: number | null;
  generationTimeMs?: number | null;
  resultImageUrl?: string | null;
  createdAt: string;
  user?: { email?: string; displayName?: string };
  style?: { name?: string };
}

export const aiStylesService = {
  async getAllStyles(): Promise<AiArtifactStyle[]> {
    return request<AiArtifactStyle[]>(`${API_BASE}/admin/ai/artifacts/styles`);
  },

  async fetchStats(): Promise<AiGenerationStats> {
    return request<AiGenerationStats>(`${API_BASE}/admin/ai/artifacts/stats`);
  },

  async fetchGenerations(page = 1, limit = 20): Promise<{ items: AiGenerationLog[]; meta: { total: number; totalPages: number } }> {
    return request<{ items: AiGenerationLog[]; meta: { total: number; totalPages: number } }>(
      `${API_BASE}/admin/ai/artifacts/generations?page=${page}&limit=${limit}`,
    );
  },

  async createStyle(payload: CreateAiStylePayload): Promise<AiArtifactStyle> {
    return request<AiArtifactStyle>(`${API_BASE}/admin/ai/artifacts/styles`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async updateStyle(
    id: string,
    payload: UpdateAiStylePayload,
  ): Promise<AiArtifactStyle> {
    return request<AiArtifactStyle>(
      `${API_BASE}/admin/ai/artifacts/styles/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  },

  async toggleActive(id: string): Promise<AiArtifactStyle> {
    return request<AiArtifactStyle>(
      `${API_BASE}/admin/ai/artifacts/styles/${encodeURIComponent(id)}/toggle`,
      {
        method: 'PATCH',
      },
    );
  },
};
